import type { FlowDefinition } from '../types';
import { resolveFlowNodeSockets } from './flowNodeDefinitions';
import { resolveFlowForBlock, resolveSectionOrPageFlow } from './flowBlockResolution';

function findButtonFlow(
  flows: FlowDefinition[],
  blockId: string,
  sectionId?: string | null
) {
  const resolvedBlockFlow = resolveFlowForBlock(flows, {
    block: { id: blockId, type: 'button' },
    sectionId,
  });
  if (resolvedBlockFlow?.nodes.some((node) => node.blockId === blockId && node.blockType === 'button')) {
    return resolvedBlockFlow;
  }
  if (sectionId) {
    return resolveSectionOrPageFlow(flows, sectionId) ?? null;
  }
  const directMatch = flows.find((flow) =>
    flow.nodes.some((node) => node.blockId === blockId && node.blockType === 'button')
  );
  if (directMatch) return directMatch;
  return resolveSectionOrPageFlow(flows, sectionId) ?? null;
}

export function resolveButtonClickActionTargets(
  flows: FlowDefinition[] | null | undefined,
  args: {
    blockId: string;
    sectionId?: string | null;
  }
) {
  if (!Array.isArray(flows)) return null;
  const flow = findButtonFlow(flows, args.blockId, args.sectionId);
  if (!flow) return null;

  const buttonNode =
    flow.nodes.find((node) => node.blockId === args.blockId && node.blockType === 'button') ?? null;
  if (!buttonNode) return null;

  const clickFieldId =
    buttonNode.outputs.find((binding) => binding.socketId === 'click')?.fieldId ?? null;
  if (!clickFieldId) return null;

  const targetNodeIds = flow.nodes
    .filter((node) => node.id !== buttonNode.id)
    .filter((node) =>
      node.inputs.some((binding) => {
        if (binding.fieldId !== clickFieldId) return false;
        const socketDefinition = resolveFlowNodeSockets(node, flow).find(
          (socket) => socket.direction === 'input' && socket.id === binding.socketId
        );
        return socketDefinition?.portKind === 'trigger';
      })
    )
    .map((node) => node.id);

  return {
    flowId: flow.id,
    buttonNodeId: buttonNode.id,
    clickFieldId,
    targetNodeIds,
  };
}
